import domReady from '@wordpress/dom-ready'
import round from 'lodash-es/round'
import { setImageOffsets } from './offsets'

/*
This filter simulates a realistic inertia effect by smoothing the incoming values

The constant adjustment of the tau value based on the time difference is
necessary in order to achieve the same interaction behaviour on devices with different
framerates, causing the requestAnimationFrame callback and therefore the filter to be called at unpredictable rate
*/

class AdaptiveLowPassFilter {
  tau: number
  previousValue: number
  lastTime = 0
  resetValue: number

  constructor(tau: number, initialValue = 0) {
    this.tau = tau
    this.previousValue = initialValue
    this.resetValue = initialValue
  }

  apply(newValue: number) {
    const currentTime = performance.now()
    const dt = this.lastTime > 0 ? currentTime - this.lastTime : 0 // time difference in ms
    this.lastTime = currentTime

    const alpha = 1 - Math.exp(-dt / this.tau)

    this.previousValue = alpha * newValue + (1 - alpha) * this.previousValue

    return this.previousValue
  }

  getPreviousValue() {
    return this.previousValue
  }

  resetTime() {
    this.lastTime = 0
  }
}

const filterX = new AdaptiveLowPassFilter(300, 0)
const filterY = new AdaptiveLowPassFilter(300, 0)

function initHoverEffect(element: HTMLElement) {
  let continueRenderLoop = false
  let lastX = 0
  let lastY = 0
  let pointerPosition = { x: 0, y: 0 }
  let returnToOrigin = false

  const update = () => {
    const { left, top } = element.getBoundingClientRect()
    const width = element.offsetWidth
    const height = element.offsetHeight
    const midX = left + width / 2
    const midY = top + height / 2
    const targetX = returnToOrigin ? 0 : (pointerPosition.x - midX) / width
    const targetY = returnToOrigin ? 0 : (pointerPosition.y - midY) / height
    const filteredX = round(filterX.apply(targetX), 5)
    const filteredY = round(filterY.apply(targetY), 5)
    // when the change of value is small enough abort the render loop
    if (
      Math.abs(filteredX - lastX) < 0.0001 &&
      Math.abs(filteredY - lastY) < 0.0001
    ) {
      continueRenderLoop = false
    }
    lastX = filteredX
    lastY = filteredY

    element.style.setProperty(
      '--pointer-perspective-x',
      `${round(filteredX * 250, 1)}px`
    )
    element.style.setProperty(
      '--pointer-perspective-y',
      `${round(filteredY * 100, 1)}px`
    )
  }

  function loop() {
    update()
    if (continueRenderLoop) {
      requestAnimationFrame(loop)
    }
  }

  function resetFilters() {
    filterX.resetTime()
    filterY.resetTime()
  }

  function onMouseEnter(evt: MouseEvent) {
    resetFilters()
    lastX = 0
    lastY = 0
    returnToOrigin = false
    continueRenderLoop = true
    pointerPosition = { x: evt.clientX, y: evt.clientY }
    requestAnimationFrame(loop)
    window.addEventListener('scroll', resetFilters)
  }

  function onMouseMove(evt: MouseEvent) {
    returnToOrigin = false
    pointerPosition = { x: evt.clientX, y: evt.clientY }
    if (!continueRenderLoop) {
      continueRenderLoop = true
      requestAnimationFrame(loop)
    }
  }

  function onMouseLeave() {
    returnToOrigin = true
    window.removeEventListener('scroll', resetFilters)
  }

  const controller = new AbortController()

  element.addEventListener('mouseenter', onMouseEnter, {
    signal: controller.signal
  })
  element.addEventListener('mousemove', onMouseMove, {
    passive: true,
    signal: controller.signal
  })
  element.addEventListener('mouseleave', onMouseLeave, {
    signal: controller.signal
  })
  // remove the mouse listeners when a touch device is used
  element.addEventListener('touchstart', () => {
    controller.abort()
  })
}

function initScrollEffect(element: HTMLElement) {
  function updateScroll() {
    const progress = calculateScrollProgress(element)
    element.style.setProperty(
      '--scroll-perspective-y',
      `${round(200 * (progress - 0.5), 1)}px`
    )
  }

  const handleScroll = () => requestAnimationFrame(updateScroll)

  function handleIntersection(entries: IntersectionObserverEntry[]) {
    const entry = entries[0]
    if (!entry) return

    if (entry.isIntersecting) {
      handleScroll()
      window.addEventListener('scroll', handleScroll, { passive: true })
    } else {
      handleScroll()
      window.removeEventListener('scroll', handleScroll)
    }
  }

  const observer = new IntersectionObserver(handleIntersection, {
    root: null
  })

  observer.observe(element)
}

function calculateScrollProgress(element: HTMLElement) {
  const rect = element.getBoundingClientRect()
  const windowHeight = window.innerHeight
  const progress = (windowHeight - rect.top) / (windowHeight + rect.height)
  return Math.min(Math.max(progress, 0), 1)
}

domReady(() => {
  const blockElement = document.querySelector<HTMLElement>(
    '.wp-block-yellowtree-fluid-gallery'
  )
  if (!blockElement) return
  const imageItems = Array.from(
    blockElement.querySelectorAll<HTMLElement>(
      '.wp-block-yellowtree-fluid-gallery-image'
    )
  )

  setImageOffsets(imageItems)
  initHoverEffect(blockElement)
  initScrollEffect(blockElement)
})
